import { AxiosError } from 'axios';

// 错误类型定义
export interface ApiError {
  code: string;
  message: string;
  details?: any;
  status?: number;
}

// 常见错误类型
export const ErrorTypes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVER_ERROR: 'SERVER_ERROR', 
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  AUTH_ERROR: 'AUTH_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  RATE_LIMIT: 'RATE_LIMIT',
  TIMEOUT: 'TIMEOUT',
  UNKNOWN: 'UNKNOWN',
} as const;

// 错误码映射
const errorCodeMap: Record<number, string> = {
  400: ErrorTypes.VALIDATION_ERROR,
  401: ErrorTypes.AUTH_ERROR,
  403: ErrorTypes.AUTH_ERROR,
  404: ErrorTypes.NOT_FOUND,
  429: ErrorTypes.RATE_LIMIT,
  500: ErrorTypes.SERVER_ERROR,
  502: ErrorTypes.SERVER_ERROR,
  503: ErrorTypes.SERVER_ERROR,
  504: ErrorTypes.TIMEOUT,
};

// 解析 Axios 错误
export function parseAxiosError(error: unknown): ApiError {
  // 如果不是 AxiosError，返回通用错误
  if (!error || typeof error !== 'object') {
    return {
      code: ErrorTypes.UNKNOWN,
      message: '发生未知错误',
      details: error,
    };
  }

  const axiosError = error as AxiosError;
  if (!axiosError.response) {
    // 网络错误
    if (axiosError.code === 'ECONNABORTED') {
      return {
        code: ErrorTypes.TIMEOUT,
        message: '请求超时，请检查网络连接',
        details: axiosError.message,
      };
    }
    
    return {
      code: ErrorTypes.NETWORK_ERROR,
      message: '网络连接失败，请检查网络设置',
      details: axiosError.message,
    };
  }

  const status = axiosError.response.status;
  const errorType = errorCodeMap[status] || ErrorTypes.UNKNOWN;
  
  // 尝试获取服务器返回的错误信息
  let message = axiosError.message;
  let details = undefined;

  const responseData = axiosError.response.data as Record<string, any>;
  if (responseData) {
    if (typeof responseData === 'string') {
      message = responseData;
    } else if (responseData.message) {
      message = responseData.message;
      details = responseData.details;
    } else if (responseData.error) {
      message = responseData.error;
    }
  }

  // 为常见状态码提供友好消息
  const friendlyMessages: Record<number, string> = {
    400: '请求参数错误，请检查输入',
    401: '未登录或登录已过期，请重新登录',
    403: '没有权限访问此资源',
    404: '请求的资源不存在',
    429: '请求过于频繁，请稍后重试',
    500: '服务器内部错误，请稍后重试',
    502: '服务暂时不可用，请稍后重试',
    503: '服务维护中，请稍后重试',
    504: '服务响应超时，请稍后重试',
  };

  return {
    code: errorType,
    message: friendlyMessages[status] || message,
    details,
    status,
  };
}

// 错误提示函数
export function showErrorToast(error: ApiError) {
  // 这里可以集成 toast 通知系统
  console.error('Error toast:', error);
  
  // 临时使用 alert 演示
  alert(`${error.message} (${error.code})`);
}

// 重试函数
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  delay = 1000,
  shouldRetry?: (error: ApiError) => boolean
): Promise<T> {
  let lastError: ApiError | null = null;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      const apiError = parseAxiosError(error as AxiosError);
      lastError = apiError;
      
      // 判断是否应该重试
      const retryable = shouldRetry ? shouldRetry(apiError) : isRetryableError(apiError);
      
      if (!retryable || attempt === maxRetries) {
        throw apiError;
      }
      
      // 指数退避延迟
      const waitTime = delay * Math.pow(2, attempt - 1);
      console.log(`Retry attempt ${attempt}/${maxRetries} after ${waitTime}ms`);
      
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }
  
  throw lastError;
}

// 判断错误是否可重试
export function isRetryableError(error: ApiError): boolean {
  const retryableCodes = [
    ErrorTypes.NETWORK_ERROR,
    ErrorTypes.TIMEOUT,
    ErrorTypes.RATE_LIMIT,
    ErrorTypes.SERVER_ERROR,
  ];
  
  return retryableCodes.includes(error.code as any);
}

// 错误恢复策略
export function getRecoveryStrategy(error: ApiError): {
  action: 'retry' | 'refresh' | 'login' | 'contact' | 'none';
  message: string;
  buttonText: string;
} {
  switch (error.code) {
    case ErrorTypes.NETWORK_ERROR:
    case ErrorTypes.TIMEOUT:
      return {
        action: 'retry',
        message: '网络连接出现问题',
        buttonText: '重试连接',
      };
    
    case ErrorTypes.AUTH_ERROR:
      return {
        action: 'login',
        message: '登录已过期',
        buttonText: '重新登录',
      };
    
    case ErrorTypes.RATE_LIMIT:
      return {
        action: 'retry',
        message: '请求过于频繁，请稍后再试',
        buttonText: '稍后重试',
      };
    
    case ErrorTypes.SERVER_ERROR:
      return {
        action: 'contact',
        message: '服务器暂时不可用',
        buttonText: '联系支持',
      };
    
    default:
      return {
        action: 'none',
        message: error.message,
        buttonText: '确定',
      };
  }
}

// 错误监控（可集成 Sentry 等）
export function trackError(error: ApiError, context?: Record<string, any>) {
  console.error('Error tracking:', { error, context });
  
  // 集成 Sentry 的示例
  // if (typeof window !== 'undefined' && (window as any).Sentry) {
  //   (window as any).Sentry.captureException(error, { extra: context });
  // }
}

// 优雅的错误边界 fallback
export function createErrorFallback(error: ApiError, onRetry?: () => void) {
  const strategy = getRecoveryStrategy(error);
  
  return (
    <div className="p-6 bg-white rounded-lg border shadow-sm">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-600 mb-4">
          {strategy.action === 'retry' ? '🔄' : '⚠️'}
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          操作失败
        </h3>
        <p className="text-gray-600 mb-4">{strategy.message}</p>
        <div className="text-sm text-gray-500 mb-6">
          错误代码: <code className="bg-gray-100 px-2 py-1 rounded">{error.code}</code>
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            {strategy.buttonText}
          </button>
        )}
      </div>
    </div>
  );
}